import { Link } from '@tanstack/react-router';
import { RENDEZ_VOUS_KIND_LABELS, type RendezVousKind } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import { useUnarchiveRendezVous } from '@/lib/rendezvous-archive';
import { formatRendezVousDate } from './upcoming-rendezvous-card';

const STATUS_LABELS: Record<string, string> = {
  confirmed: 'Terminé',
  cancelled: 'Annulé',
  expired: 'Expiré (non payé)',
  refunded: 'Remboursé',
};

/** Forme générique côté élève ET côté tuteur — cf. `UpcomingRendezVousItem`. */
export interface PastRendezVousItem {
  reservationId: string;
  otherPartyName: string;
  startsAt: string;
  kind: RendezVousKind;
  status: string;
  archivedByMe: boolean;
}

/**
 * Carte "rendez-vous passé" (terminé, annulé, ou archivé manuellement) —
 * point d'accès aux notes / au résumé d'entretien (via `/rendez-vous/$reservationId`,
 * qui affiche `visio_room_notes` si présentes). Un rendez-vous archivé
 * (migration 0240) reste "Restaurable" depuis ici.
 */
export function PastRendezVousCard({ item }: { item: PastRendezVousItem }) {
  const { user } = useAuth();
  const unarchive = useUnarchiveRendezVous(user?.id);

  return (
    <div className="border-ink-100 flex flex-col gap-3 rounded-2xl border bg-white p-4 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <p className="text-ink-900 truncate text-sm font-semibold">{item.otherPartyName}</p>
        <p className="text-ink-500 mt-0.5 text-xs first-letter:uppercase">
          {formatRendezVousDate(item.startsAt)}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <span className="bg-ink-100 text-ink-600 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold">
            {RENDEZ_VOUS_KIND_LABELS[item.kind]}
          </span>
          <span className="bg-ink-100 text-ink-500 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold">
            {STATUS_LABELS[item.status] ?? item.status}
          </span>
          {item.archivedByMe && (
            <span className="bg-accent-50 text-accent-700 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold">
              Archivé
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {item.archivedByMe && (
          <button
            type="button"
            onClick={() => unarchive.mutate(item.reservationId)}
            disabled={unarchive.isPending}
            className="text-primary-600 min-h-[2.75rem] font-sans text-xs font-semibold underline active:scale-95 disabled:opacity-50"
          >
            Restaurer
          </button>
        )}
        <Link
          to="/rendez-vous/$reservationId"
          params={{ reservationId: item.reservationId }}
          className="border-ink-200 text-ink-700 hover:bg-ink-50 flex min-h-[2.75rem] items-center rounded-xl border px-3 text-xs font-semibold transition-colors active:scale-95"
        >
          Voir
        </Link>
      </div>
    </div>
  );
}
